/**
 * Custom hooks are stateful, reusable chunks of logic that we can use in functional components
 * Handy to cut down on repetitive boilerplate
 */
import { useState, useEffect, useMemo, useCallback } from 'react'
import { useLocation, useHistory } from 'react-router-dom';

/**
 * This hook handles pagination state, for when we don't want to use url search params (e.g. for infinite scroll?)
 * @param {object} initialPagination - a pagination object, default is { page: 1, per: 10 }
 * @returns the pagination object and `setPage` and `setPer` functions
 */
export const usePagination = (initialPagination = {}) => {
  // use the built-in `useState` hook to handle state
  const [pagination, setPagination] = useState(initialPagination);

  // create specific actions to update pagination
  const setPage = newPage => {
    setPagination(state => ({ ...state, page: newPage || 1 }));
  }

  const setPer = newPer => {
    // reset the page to 1 when we change the per because the number of pages will change (and could be smaller than the current page)    
    setPagination(state => ({ page: 1, per: newPer || 10 }));
  }

  return { ...pagination, setPage, setPer };
}

/**
 * 
 * @returns {boolean} true if the window is focused, false otherwise
 */
export const useIsFocused = () => {
  const [isFocused, setIsFocused] = useState(document.visibilityState === 'visible');
  const onFocus = () => setIsFocused(true);
  const onBlur = () => setIsFocused(false);

  useEffect(() => {
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  return isFocused;
}

/**
 * 
 * @param {object} defaultValues - an object of default values to assign to the url search params (if they aren't already there)
 * @param {object} options - an object of options to customize the behavior of the hook
 * @param {boolean} options.allowToggle - if true, allows removing params by omitting them when passing an object to `setURLParams`, defaults to false meaning params not included in the object will be left unchanged
 * @returns {[queryObject: queryObject, setURLParams: Function]} an array with an object containing the search params as key/value pairs and a function to update the search params object
 */
export const useURLSearchParams = (defaultValues = {}, options = {}) => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // on mount, set default values
  useEffect(() => {
    Object.keys(defaultValues).forEach(key => {
      if(!searchParams.has(key)) {
        searchParams.set(key, defaultValues[key].toString());
      }
    });
    // Normalize comparison by stripping/adding leading '?'
    const nextQS = searchParams?.toString() || '';
    const currentQS = location.search?.startsWith('?') ? location.search.slice(1) : (location.search || '');
    if(nextQS !== currentQS) {
      history.replace({ search: nextQS ? `?${nextQS}` : '' });
    }
  }, []);

  // return an object of key value pairs matching the search params to be used in list query
  const queryObject = useMemo(() => {
    const obj = {};
    searchParams?.forEach((value, key) => {
      obj[key] = value;
    });
    return obj;
  }, [searchParams]);

  // accepts:
  // an object of key/value pairs to update - useful for updating multiple params at once ex: setURLParams({ page: 2, per: 20 })
  // OR a single key/value pair as two arguments - useful for updating a single param ex: setURLParams('page', 2)
  // OR an updater function that receives the current queryObject and returns an updated object (to mimic standard react setState functional updates) - ex: setURLParams(current => ({ ...current, page: 2 }))
  // the function is useful when you want to preserve existing params that aren't being updated, but you have `allowToggle` enabled which would normally remove anything not included in the object
  const setURLParams = useCallback((...args) => {
    if(args.length === 2) {
      const [key, value] = args;
      if(searchParams?.get(key) !== value) {
        searchParams?.set(key, value);
      }
      // compare the next and current query strings before pushing to history (avoid loops)
      const nextQS = searchParams?.toString() || '';
      const currentQS = location.search?.startsWith('?') ? location.search.slice(1) : (location.search || '');
      if(nextQS !== currentQS) {
        history.push({ search: nextQS ? `?${nextQS}` : '' });
      }
    } else if(args.length === 1 && typeof args[0] === 'object') {
      const [obj] = args;
      Object.keys(obj).forEach(key => {
        if(searchParams?.get(key) !== obj[key]) {
          searchParams?.set(key, obj[key]);
        }
      });
      if(options.allowToggle) {
        // make sure to remove existing keys that aren't in the new object
        Object.keys(queryObject).forEach(existingKey => {
          if(!(existingKey in obj)) {
            searchParams.delete(existingKey);
          }
        });
      }
      // compare the next and current query strings before pushing to history
      const nextQS = searchParams?.toString() || '';
      const currentQS = location.search?.startsWith('?') ? location.search.slice(1) : (location.search || '');
      if(nextQS !== currentQS) {
        history.push({ search: nextQS ? `?${nextQS}` : '' });
      }
    } else if(args.length === 1 && typeof args[0] === 'function') {
      const [updaterFn] = args;
      const updatedValues = updaterFn(queryObject);
      Object.keys(updatedValues).forEach(key => {
        if(searchParams?.get(key) !== updatedValues[key]) {
          searchParams?.set(key, updatedValues[key]);
        }
      });
      if(options.allowToggle) {
        // make sure to remove existing keys that aren't in the new object
        Object.keys(queryObject).forEach(existingKey => {
          if(!(existingKey in updatedValues)) {
            searchParams.delete(existingKey);
          }
        });
      }
      // compare the next and current query strings before pushing to history
      const nextQS = searchParams?.toString() || '';
      const currentQS = location.search?.startsWith('?') ? location.search.slice(1) : (location.search || '');
      if(nextQS !== currentQS) {
        history.push({ search: nextQS ? `?${nextQS}` : '' });
      }
    } else {
      throw new Error('useURLSearchParams: `handleChange` must be called with either a key/value pair or an object of key/value pairs, received: ' + args + '.');
    }
  }, [searchParams, history, location.search]);


  return [queryObject, setURLParams];
}

export const usePageTitle = (title) => {
  const location = useLocation();
  useEffect(() => {
    if(typeof title === 'string' && title.trim()) {
      document.title = title;
    }
    // If title is falsy/empty, do nothing so earlier titles aren't overwritten on mount
  }, [title, location.pathname]);
}